import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updatePatientAllergy } from '../../../Redux/AllergySlice';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

const EditAllergyModal = ({ isOpen, onClose, allergyToEdit, hasNoKnownAllergies }) => {
  const dispatch = useDispatch();
  const { patientId } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [currentAllergy, setCurrentAllergy] = useState({
    name: '',
    onset_date: '',
    reaction: ''
  });
  const [noKnownAllergies, setNoKnownAllergies] = useState(false);
  const [showNoKnownAllergiesCheckbox, setShowNoKnownAllergiesCheckbox] = useState(false);
  const [initialState, setInitialState] = useState(null);

  useEffect(() => {
    if (allergyToEdit) {
      if (allergyToEdit.no_known_allergies) {
        const today = new Date().toISOString().split('T')[0];
        const newState = {
          name: '',
          onset_date: today,
          reaction: '',
          no_known_allergies: true
        };
        setCurrentAllergy(newState);
        setNoKnownAllergies(true);
        setShowNoKnownAllergiesCheckbox(true);
        setInitialState(newState);
      } else {
        const formattedOnsetDate = allergyToEdit.onset_date 
          ? new Date(allergyToEdit.onset_date).toISOString().split('T')[0]
          : '';
          
        const newState = {
          name: allergyToEdit.name || '',
          onset_date: formattedOnsetDate,
          reaction: allergyToEdit.reaction || '',
          no_known_allergies: false
        };
        setCurrentAllergy(newState);
        setNoKnownAllergies(false);
        setShowNoKnownAllergiesCheckbox(false);
        setInitialState(newState);
      }
    }
  }, [allergyToEdit]);

  const handleNoKnownAllergiesChange = (e) => {
    setNoKnownAllergies(e.target.checked);
    if (!e.target.checked) {
      setShowNoKnownAllergiesCheckbox(false);
    }
  };

  const hasChanges = () => {
    if (!initialState) return false;
    
    if (noKnownAllergies !== initialState.no_known_allergies) return true;
    
    if (!noKnownAllergies) {
      return (
        currentAllergy.name !== initialState.name ||
        currentAllergy.onset_date !== initialState.onset_date ||
        currentAllergy.reaction !== initialState.reaction
      );
    }
    
    return false;
  };

  const handleSaveChanges = async () => {
    try {
      if (!hasChanges()) {
        toast.info('No changes were made');
        onClose();
        return;
      }

      setIsSaving(true);
      let allergyData;
      
      if (noKnownAllergies) {
        const currentDate = new Date().toISOString().split("T")[0];
        allergyData = {
          no_known_allergies: true,
          name: "No Known Allergies",
          reaction: "None",
          onset_date: currentDate,
        };
      } else {
        if (!currentAllergy.name.trim() || !currentAllergy.reaction.trim() || !currentAllergy.onset_date) {
          toast.error('Please fill in all allergy fields');
          setIsSaving(false);
          return;
        }
        
        allergyData = {
          ...currentAllergy,
          no_known_allergies: false
        };
      }
      
      await dispatch(updatePatientAllergy({
        patientId,
        allergyId: allergyToEdit.id,
        allergies: allergyData
      })).unwrap();
      
      onClose();
    } catch (error) {
      console.error('Error updating allergy:', error);
      toast.error('Failed to update allergy');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !allergyToEdit) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="mb-6 text-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {allergyToEdit.no_known_allergies ? 'Edit No Known Allergies Status' : 'Edit Allergy'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {allergyToEdit.no_known_allergies 
              ? 'Uncheck the box below to add specific allergy details'
              : 'Modify details for this allergy'}
          </p>
        </div>

        {showNoKnownAllergiesCheckbox && (
          <div className="mb-6">
            <label className="flex items-center space-x-2 font-medium text-sm">
              <input
                type="checkbox"
                checked={noKnownAllergies}
                onChange={handleNoKnownAllergiesChange}
                className="form-checkbox h-5 w-5 text-green-500 focus:ring-green-500 focus:border-green-500"
              />
              <span className={noKnownAllergies ? "text-green-600 font-semibold" : ""}>No Known Allergies</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-7">
              Uncheck this to add specific allergy details below
            </p>
          </div>
        )}

        {!noKnownAllergies && (
          <div className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="editName" className="block font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="editName"
                value={currentAllergy.name}
                onChange={(e) => setCurrentAllergy({...currentAllergy, name: e.target.value})}
                className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter allergy name"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="editOnsetDate" className="block font-medium text-gray-700">
                Onset Date <span className="text-red-500">*</span>
              </label>
              <input
                id="editOnsetDate"
                type="date"
                max={new Date().toISOString().split("T")[0]}
                value={currentAllergy.onset_date}
                onChange={(e) => setCurrentAllergy({...currentAllergy, onset_date: e.target.value})}
                className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="editReaction" className="block font-medium text-gray-700">
                Reaction <span className="text-red-500">*</span>
              </label>
              <textarea
                id="editReaction"
                value={currentAllergy.reaction}
                onChange={(e) => setCurrentAllergy({...currentAllergy, reaction: e.target.value})}
                className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Describe the allergic reaction"
                rows="3"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end mt-8 space-x-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 border-red-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Updating...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAllergyModal;